import {useEffect, useState} from "react";
import {Navigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {cn} from "@/lib/utils";
import {Loader} from "@/src/modules/core/components";
import {PageHeader} from "@/src/modules/core/components/page-header";
import {isDispatchOperatorRole} from "@/src/modules/core/lib/dispatch-center-policy";
import {useStoredMe} from "@/src/modules/core/store/me";

import {useActiveShift} from "@/src/modules/officer/hooks";

import {DispatchCoverageEmpty} from "../components/dispatch-coverage-empty";
import {DispatchDeskShiftChip} from "../components/dispatch-desk-shift-chip";
import {DispatchPropertyFinder} from "../components/dispatch-home-property-switcher";
import {DispatchOnDutyList} from "../components/dispatch-on-duty-list";
import {DispatchPropertyEmergencyContacts} from "../components/dispatch-property-emergency-contacts";
import {DispatchPropertyGeneralInfo} from "../components/dispatch-property-general-info";
import {DispatchOnGuardOfficers} from "../components/dispatch-on-guard-officers";
import {DispatchPropertyManagers} from "../components/dispatch-property-managers";
import {DispatchPropertyStaffRow} from "../components/dispatch-property-staff-row";
import {DispatchPropertyRules} from "../components/dispatch-property-rules";
import {DispatchQuickActions} from "../components/dispatch-quick-actions";
import {useDispatchCoverageRows} from "../hooks/use-dispatch-coverage-rows";
import {useDispatchDeskScrollLock} from "../hooks/use-dispatch-desk-scroll-lock";
import {showDispatchPersonalShiftBlock, isDispatchDeskShiftLocked} from "../lib/dispatch-home-policy";
import {
  DISPATCH_DESK_ACTIONS_COLUMN_CLASS,
  DISPATCH_DESK_BOTTOM_ROW_PANEL_CLASS,
  DISPATCH_DESK_FINDER_SLOT_CLASS,
  DISPATCH_DESK_FINDER_WIDTH_CLASS,
  DISPATCH_DESK_GRID_CLASS,
  DISPATCH_DESK_GRID_EMERGENCY_CLASS,
  DISPATCH_DESK_GRID_GENERAL_INFO_CLASS,
  DISPATCH_DESK_GRID_RULES_CLASS,
  DISPATCH_DESK_MOBILE_STACK_CLASS,
  DISPATCH_DESK_PAGE_BODY_CLASS,
  DISPATCH_DESK_PAGE_HEADER_CLASS,
  DISPATCH_DESK_PAGE_HEADER_TOP_ROW_CLASS,
  DISPATCH_DESK_PAGE_SHELL_CLASS,
  DISPATCH_DESK_TOP_ROW_PANEL_CLASS,
} from "../lib/dispatch-desk-layout";

export default function DispatchHomePage() {
  const {t} = useTranslation("dispatch_desk");
  const me = useStoredMe();
  const showPersonalShift = showDispatchPersonalShiftBlock(me);
  const {
    shift: activeShift,
    isPending: activeShiftPending,
    isSuccess: activeShiftResolved,
  } = useActiveShift();
  const {rows, hasCoverage, isPending, isFetching, refetch} = useDispatchCoverageRows();
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);

  useDispatchDeskScrollLock();

  const requireActiveShiftForQuickActions = showPersonalShift;

  useEffect(() => {
    if (selectedShiftId === null) return;
    if (!rows.some((row) => row.shift_id === selectedShiftId)) {
      setSelectedShiftId(null);
    }
  }, [rows, selectedShiftId]);

  if (!isDispatchOperatorRole(me)) {
    return <Navigate to="/home" replace />;
  }

  if (showPersonalShift && activeShiftPending) {
    return (
      <div className={DISPATCH_DESK_PAGE_SHELL_CLASS}>
        <Loader className="min-h-[12rem] flex-1" />
      </div>
    );
  }

  if (showPersonalShift && activeShiftResolved && isDispatchDeskShiftLocked(showPersonalShift, false, activeShift)) {
    return <Navigate to="/home" replace />;
  }

  const quickActionsKey = showPersonalShift ? (activeShift?.shift_id ?? "dispatch-desk") : "dispatch-desk";
  const quickActionsProps = {
    isOnActiveShift: showPersonalShift ? Boolean(activeShift) : true,
    requireActiveShift: requireActiveShiftForQuickActions,
  } as const;

  const onDutyPanel = isPending ? (
    <Loader className="min-h-[10rem] flex-1 rounded-lg border bg-card lg:min-h-0" />
  ) : hasCoverage ? (
    <DispatchOnDutyList
      rows={rows}
      selectedShiftId={selectedShiftId}
      onSelectShift={setSelectedShiftId}
      isPending={isPending}
      isFetching={isFetching}
      onRefresh={() => void refetch()}
      className="min-h-0 flex-1"
    />
  ) : (
    <DispatchCoverageEmpty className="min-h-0 flex-1" />
  );

  return (
    <div className={DISPATCH_DESK_PAGE_SHELL_CLASS}>
      <PageHeader className={DISPATCH_DESK_PAGE_HEADER_CLASS}>
        <div className={DISPATCH_DESK_PAGE_HEADER_TOP_ROW_CLASS}>
          <PageHeader.TitleWrapper className="order-1 min-w-0 md:shrink-0">
            <PageHeader.Title title={t("title")} />
          </PageHeader.TitleWrapper>

          {showPersonalShift ? (
            <div className="order-3 flex shrink-0 justify-end">
              <DispatchDeskShiftChip />
            </div>
          ) : null}
        </div>

        <div className={DISPATCH_DESK_FINDER_SLOT_CLASS}>
          <div className={DISPATCH_DESK_FINDER_WIDTH_CLASS}>
            <DispatchPropertyFinder showStartShiftHint={showPersonalShift} className="w-full" />
          </div>
        </div>
      </PageHeader>

      <div className={DISPATCH_DESK_PAGE_BODY_CLASS}>
        <div className={DISPATCH_DESK_MOBILE_STACK_CLASS}>
          <DispatchPropertyGeneralInfo />
          {onDutyPanel}
          <DispatchOnGuardOfficers />
          <DispatchQuickActions key={`${quickActionsKey}-mobile`} className="shrink-0" {...quickActionsProps} />
          <DispatchPropertyEmergencyContacts />
          <DispatchPropertyRules />
          <DispatchPropertyManagers />
        </div>

        <div className={cn(DISPATCH_DESK_GRID_CLASS, "hidden lg:grid")}>
          <DispatchPropertyGeneralInfo
            className={[DISPATCH_DESK_TOP_ROW_PANEL_CLASS, DISPATCH_DESK_GRID_GENERAL_INFO_CLASS].join(" ")}
          />
          <DispatchPropertyStaffRow />
          <DispatchPropertyEmergencyContacts
            className={[DISPATCH_DESK_BOTTOM_ROW_PANEL_CLASS, DISPATCH_DESK_GRID_EMERGENCY_CLASS].join(" ")}
          />
          <DispatchPropertyRules
            className={[DISPATCH_DESK_BOTTOM_ROW_PANEL_CLASS, DISPATCH_DESK_GRID_RULES_CLASS].join(" ")}
          />
          <div className={DISPATCH_DESK_ACTIONS_COLUMN_CLASS}>
            <DispatchQuickActions key={`${quickActionsKey}-desktop`} className="shrink-0" {...quickActionsProps} />
            {onDutyPanel}
          </div>
        </div>
      </div>
    </div>
  );
}
